import React, { useEffect, useMemo, useState } from 'react';
import Styles from './Menu.module.scss';
import type MenuItem from '../../Models/MenuItem';
import { useTranslation } from 'react-i18next';
import * as API from '../../API';
import useFavoritesState from '../../Hooks/UseFavoritesState';
import EIDButton from '../EIDButton/EIDButton';
import FavoritesMenu from '../FavoritesMenu/FavoritesMenu';
import FilingCabinet from '../FilingCabinet/FilingCabinet';
import FullMenu from '../FullMenu/FullMenu';
import { useUserContext } from '@/Contexts/Authenticated/UserContext';
import { useSystemSettingsContext } from '@/Contexts/App/SystemSettingsContext';
import { GlobalEvent, useEventContext } from '@/Contexts/App/EventContext';
import { DobInput } from '@dedalusdiit/dob-components';

export const DraggedMenuItemContext = React.createContext<{ get: Function; set: Function }>({ get: () => {}, set: () => {} });

function Menu({ isVisible }: Readonly<{ isVisible: boolean }>): React.ReactElement {
    const { t } = useTranslation();
    const { user } = useUserContext();
    const eventContext = useEventContext();
    const settingsContext = useSystemSettingsContext();
    const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
    const [favorites, setFavorites] = useFavoritesState([]);
    const [hidden, setHidden] = useState<boolean>(false);
    const [search, setSearch] = useState('');

    let dragged: MenuItem;

    const draggedContextValue = useMemo(() => ({
        set: (d: MenuItem) => {
            dragged = d; 
        },
        get: () => dragged,
    }), []);

    useEffect(() => {
        getMenuAndFavoriteItems();

        eventContext.subscribe(GlobalEvent.CustomWorklistsChanged, getMenuAndFavoriteItems);

        return () => {
            eventContext.unsubscribe(GlobalEvent.CustomWorklistsChanged, getMenuAndFavoriteItems);
        };
    }, []);

    const getMenuAndFavoriteItems = (): void => {
        void Promise.all([API.general.getMenuItems(), API.user.getFavorites()])
            .then(([fullRes, favRes]) => {
                if (favRes.Allowed === false || !fullRes.MenuItems || fullRes.Error) {
                    setHidden(false);
                    return;
                }

                setFavorites(favRes.Favorites, true);

                // Do some processing on the favorites vs all menu items so that we can show if an items is already added to favorites or not
                updateMenuItemsFavorite(fullRes.MenuItems, favRes.Favorites);
            });
    };

    const updateMenuItemsFavorite = (menuItems: MenuItem[], favorites: MenuItem[]): void => {
        const favIDs = favorites.map((x: MenuItem) => x.Text);
        menuItems.forEach((mi: MenuItem) => {
            mi.InFavorites = favIDs.includes(mi.Text);

            if (mi.MenuItemSub?.length) {
                mi.MenuItemSub.forEach((smi: MenuItem) => {
                    smi.InFavorites = favIDs.includes(smi.Text);
                });
            }
        });
        setMenuItems(menuItems);
    };

    const onAdd = (e: any, item: MenuItem): void => {
        e.stopPropagation();
        e.preventDefault();
        if (favorites.filter((x) => x.Text === item.Text).length) {
            return;
        }
        const newList = [...favorites, item];
        setFavorites(newList);
        updateMenuItemsFavorite(menuItems, newList);
    };

    const onRemove = (e: any, item: MenuItem): void => {
        e.stopPropagation();
        e.preventDefault();
        const newList = favorites.filter((x) => x.Text !== item.Text);
        setFavorites(newList);
        updateMenuItemsFavorite(menuItems, newList);
    };

    const onOrder = (event: any): void => {
        const { source } = event.operation;
        const list = Array.from(favorites);

        const origIndex = list.findIndex((item) => item.ID === source.id);
        const newIndex = source.index;

        const [removed] = list.splice(origIndex, 1);
        list.splice(newIndex, 0, removed);
        setFavorites(list);
    };

    const filingCabinetBelow = settingsContext.getValue('PositionFilingCabinet') === 'Below';

    const handleSearch = (value: string): void => {
        setSearch(value);
    };

    return (
        <div id={Styles.leftNav} className="scrollableContent" style={{ display: (!isVisible || hidden) ? 'none' : 'initial' }}>
            { user.showReadEIDButton
                ? (
                        <div className={Styles.eidButton}>
                            <EIDButton />
                        </div>
                    )
                : null }
            <DobInput placeholder={t('Search') ?? 'Search'} className={Styles.searchField} value={search} onValueChange={handleSearch} icon="search" />
            { !filingCabinetBelow && <FilingCabinet filter={search} /> }
            <DraggedMenuItemContext.Provider value={draggedContextValue}>
                <FavoritesMenu title={t('Favorites')} menuItems={favorites} onAdd={onAdd} onRemove={onRemove} onOrder={onOrder} filter={search} />
                <FullMenu title={t('AllItems')} menuItems={menuItems} addToFavorites={onAdd} filter={search} defaultHidden={true} />
            </DraggedMenuItemContext.Provider>
            { filingCabinetBelow && <FilingCabinet filter={search} /> }
        </div>
    );
}

export default Menu;
